import React, { useState, useRef } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WhisperSpeechToText = ({ onTranscript }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorder = useRef(null);
  const audioChunks = useRef([]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorder.current = new MediaRecorder(stream);
      audioChunks.current = [];

      mediaRecorder.current.ondataavailable = (event) => {
        audioChunks.current.push(event.data);
      };

      mediaRecorder.current.onstop = async () => {
        const audioBlob = new Blob(audioChunks.current, { type: 'audio/webm' });
        await sendToWhisper(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.current.start();
      setIsRecording(true);
      toast.info('Recording... Click again to stop');
    } catch (error) {
      console.error('Microphone error:', error);
      toast.error('Microphone access denied or not available');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
    }
  };

  const sendToWhisper = async (audioBlob) => {
    setIsProcessing(true);
    try {
      const sessionToken = localStorage.getItem('session_token');
      const formData = new FormData();
      formData.append('file', audioBlob, 'recording.webm');

      const response = await axios.post(`${API}/speech-to-text`, formData, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Whisper response:', response.data);
      
      // Extract text from response
      const transcription = response.data.text || response.data;
      
      // Make sure it's a string
      const textToAdd = typeof transcription === 'string' ? transcription : String(transcription);
      
      console.log('Transcription text:', textToAdd);
      
      onTranscript(textToAdd);
      toast.success('Speech transcribed!');
    } catch (error) {
      console.error('Transcription error:', error);
      toast.error('Failed to transcribe. Try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  if (isProcessing) {
    return (
      <button
        type="button"
        disabled
        className="p-2 rounded-lg bg-amber-100"
      >
        <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={toggleRecording}
      className={`p-2 rounded-lg transition-colors ${
        isRecording ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
      title={isRecording ? 'Stop recording' : 'Click to speak'}
    >
      {isRecording ? (
        <MicOff className="w-5 h-5 animate-pulse" />
      ) : (
        <Mic className="w-5 h-5" />
      )}
    </button>
  );
};

export default WhisperSpeechToText;
